// External Imports
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Internal Imports
import { formatCurrency, toTitleCase, truncate } from "../../util/format-text";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import { UtilityContext } from "../../context/util-context";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import PersonCard from "../UI/PersonCard";
import Notes from "../UI/Notes";
import UpdateExpenseModal from "./UpdateExpenseModal";
import { MdRefresh } from "react-icons/md";
import { DataContext } from "../../context/data-context";
import DetailList from "../UI/DetailList";
import AuditLogs from "../UI/AuditLogs";

/** ========================================================================
 * * Expense Information Page
 * Renders a page, which displays all information about an expense.
 * ========================================================================== */
export default function ExpenseInfo() {
  // Load Context
  const { goBack, setConfirm } = useContext(UtilityContext);
  const { admin } = useContext(DataContext);

  // Get ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [expense, setExpense] = useState({});
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);

  // =================== FETCH PAYMENT DATA ===================
  const fetchExpense = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "expense",
        id: id,
        columns: [
          "createdBy { name photo id type }",
          "paidBy { name photo id type }",
          "requestedBy { name photo id email }",
          "vehicle { id make { name } model year vin }",
          "application {id, make, model, year, vin}",
          "partner { id name }",
          "employee { id name }",
          "location { id address }",
          "maintenance { receipt description }",
          "supplier { name }",
          "transaction { bank date description }",
        ],
      });
      setExpense(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== DELETE EXPENSE ===================
  const deleteHandler = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("update-expense", {
        id: id,
        toDelete: true,
      });
      goBack();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== USE_EFFECT ===================
  useEffect(() => {
    fetchExpense();
  }, []);

  if (!id || (!loading && (!expense || !expense.id)))
    return (
      <div className="w-full h-full p-8 rounded-lg bg-stone-900">
        <p className="text-stone-400">
          Expense ID Not Provided or Expense Not Found!
        </p>
      </div>
    );

  return (
    <div className="divider">
      <UpdateExpenseModal
        open={edit}
        setOpen={setEdit}
        expense={expense}
        onComplete={fetchExpense}
      />
      <div className="container lg:col-span-3">
        <Heading title="Expense Details" id={expense?.id}>
          <AuditLogs table="expense" id={id} />
          <MdRefresh onClick={fetchExpense} className="icon" />
        </Heading>
        <div className="info lg:grid-cols-3">
          <DetailPair
            label="Date Created"
            value={isoDateToString(expense?.dateCreated, "MM/dd/yyyy")}
            loading={loading}
          />
          <DetailPair
            label="Date Due"
            value={isoDateToString(expense?.dueDate, "MM/dd/yyyy")}
            loading={loading}
          />
          <DetailPair
            label="Date Paid"
            value={isoDateToString(expense?.datePaid, "MM/dd/yyyy")}
            loading={loading}
          />

          <DetailPair
            label="Category"
            classes="font-semibold"
            value={toTitleCase(
              expense.category?.split("-")[1] || expense.category,
            )}
            loading={loading}
          />
          <DetailPair
            label="Status"
            classes={
              expense.status === "succeeded"
                ? "text-green-600 font-bold text-lg"
                : "text-yellow-500 font-bold text-lg"
            }
            value={expense.status?.toUpperCase()}
            loading={loading}
          />
          <DetailPair
            label="Amount"
            classes={"font-bold text-lg"}
            value={formatCurrency(expense?.amount)}
            loading={loading}
          />
          <LinkPair
            label="Attachment"
            link={expense?.attachment || expense.maintenance?.receipt}
            loading={loading}
          />
          <LinkPair label="Receipt" link={expense?.receipt} loading={loading} />
          <DetailPair
            label="Paid Via"
            classes={"font-semibold"}
            value={toTitleCase(expense.paidVia?.replace("_", ""))}
            loading={loading}
          />
          {expense?.vehicleId && (
            <DetailPair
              label="Vehicle"
              link={`/vehicles/info?id=${expense?.vehicleId}`}
              value={`${expense?.vehicle?.year} ${
                expense?.vehicle?.make?.name
              } ${expense?.vehicle?.model} ${expense?.vehicle?.vin?.substring(
                13,
                17,
              )}`}
              loading={loading}
            />
          )}
          {expense?.locationId && (
            <DetailPair
              label="Location"
              link={`/locations/info?id=${expense?.locationId}`}
              value={expense?.location?.address}
              loading={loading}
            />
          )}
          {expense?.partnerId && (
            <DetailPair
              label="Partner"
              link={`/partners/info?id=${expense?.partnerId}`}
              value={expense?.partner?.name}
              loading={loading}
            />
          )}
          {expense?.applicationId && (
            <DetailPair
              label="Application"
              link={`/applications/info?id=${expense?.applicationId}`}
              value={`${expense?.application?.year} ${
                expense?.application?.make
              } ${
                expense?.application?.model
              } ${expense?.application?.vin?.substring(13, 17)}`}
              loading={loading}
            />
          )}
          {expense?.maintenanceId && (
            <DetailPair
              label="Maintenance"
              link={`/maintenance/info?id=${expense?.maintenanceId}`}
              value={truncate(expense.maintenance?.description, 30)}
              loading={loading}
            />
          )}

          <DetailPair
            label="Supplier"
            classes="font-semibold"
            value={expense?.supplier?.name}
            loading={loading}
          />
          <div className="lg:col-span-2">
            <DetailPair
              label="Description"
              classes="font-semibold"
              value={expense?.description}
              loading={loading}
            />
          </div>
        </div>
        <Notes table="expense" id={id} />
      </div>
      <div className="sidebar">
        <div className="grid gap-8">
          {expense.createdBy && (
            <PersonCard
              label="Created By:"
              name={expense?.createdBy?.name}
              secondary={expense.createdBy?.type}
              image={expense?.createdBy?.photo}
              link={`/employees/info?id=${expense?.createdBy?.id}`}
              loading={loading}
            />
          )}
          {expense.requestedBy && (
            <PersonCard
              label="Requested By:"
              name={expense?.requestedBy?.name}
              secondary={expense.requestedBy?.email}
              image={expense?.requestedBy?.photo}
              link={`/partners?id=${expense?.requestedBy?.id}`}
              loading={loading}
            />
          )}
          {expense.paidBy && (
            <PersonCard
              label="Paid By:"
              name={expense?.paidBy?.name}
              secondary={expense.paidBy?.type}
              image={expense?.paidBy?.photo}
              link={`/employees/info?id=${expense?.paidBy?.id}`}
              loading={loading}
            />
          )}
          {expense.transactionId && (
            <DetailList
              loading={loading}
              label="Associated Transaction"
              link={`/transactions/info?id=${expense.transactionId}`}
              values={[
                expense.transaction.bank.toUpperCase(),
                expense.transaction.date,
                truncate(expense.transaction.description),
              ]}
            />
          )}
        </div>

        <div className="grid gap-4">
          {expense.status === "pending" && (
            <div className="btn-primary" onClick={() => setEdit(true)}>
              Mark As Paid
            </div>
          )}
          {expense.status !== "succeeded" && (
            <div
              className="btn-warning"
              onClick={() =>
                setConfirm({
                  message: "This action is not reversible.",
                  action: deleteHandler,
                })
              }
            >
              Delete Expense
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
